/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/require-await */

/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { DataChannel, LogEntry } from "../../../models";
import { LogEntryLevel } from "../../../models/logEntry.model";
import { stringifyError } from "../../../utils/stringifyError";
import { WebinarServiceRB } from "../webinars.service";
import { ContactService } from "../../core/contact.service";
import { WebinarTelemetryDatabase } from "./webinarTelemetryDatabase";
import { WebinarDB, WebinarLogEntryDB, WebinarSessionDB, WebinarUserDB } from "./webinarTelemetry.model";
import { JsonRPCService } from "../../telemetry/jsonRPC.service";
import { Service } from "../../service";
import { LoggerService } from "../../core";
import { TelemetryService } from "../../telemetry";

export const WEBINAR_TELEMETRY_SVC = 'WebinarTelemetryService';

export class WebinarTelemetryService extends Service {
    public static getInstance() { return Service.create(WEBINAR_TELEMETRY_SVC, WebinarTelemetryService); }
    public static build() { return new WebinarTelemetryService(); }
    private constructor() { super(); }

    private handlers: Record<string, (webinar: WebinarDB, item: LogEntry) => Promise<WebinarLogEntryDB | undefined>> = {};
    private webinarIds: Record<string, WebinarDB> = {};
    private currentUser?: WebinarUserDB;
    private currentWebinar?: WebinarDB;
    private currentSession?: WebinarSessionDB;
    private webinarServiceRB = WebinarServiceRB.getInstance();
    private loggerService = LoggerService.getInstance();
    private telemetryService = TelemetryService.getInstance();
    private jsonRPCService = JsonRPCService.getInstance();
    private contactService = ContactService.getInstance();
    private surveyedWebinarId?: string;
    private webinarTelemetryDatabase: WebinarTelemetryDatabase;
    private ongoingWebinarPromises: Record<string, Promise<WebinarDB>> = {};

    public async start(): Promise<void> {
        this.loggerService.info(`[WebinarTelemetryService] start ()`);
        this.webinarTelemetryDatabase = WebinarTelemetryDatabase.create(this.loggerService, this.webinarServiceRB);
        await this.webinarTelemetryDatabase.openDatabase();
        this.telemetryService.addSupervisedService('webinarService', this);
        this.populateHandlers();
        this.loggerService.info(`[WebinarTelemetryService] start () -- success`);
    }

    public async stop(): Promise<void> {
        this.loggerService.info(`[WebinarTelemetryService] stop ()`);
    }

    /* ************************************************************** */
    /* **  PUBLIC RPC METHODS                                      ** */
    /* ************************************************************** */
    public async getWebinars(): Promise<WebinarDB[]> {
        this.loggerService.info(`[WebinarTelemetryService] getWebinars ()`);
        return await this.webinarTelemetryDatabase.getWebinarsAndSessions();
    }

    public async getSession(params: any): Promise<WebinarSessionDB> {
        this.loggerService.info(`[WebinarTelemetryService] getSession (${params.sessionId})`);
        const session = await this.webinarTelemetryDatabase.getSessionAndLogs(params.sessionId);
        this.surveyedWebinarId = session.webinarId;
        return session;
    }

    public async getUser(): Promise<WebinarUserDB> {
        if (!this.currentUser) this.currentUser = WebinarUserDB.createFromUserRB(this.contactService.userContact);
        return this.currentUser;
    }

    /* ************************************************************** */
    /* **  PUBLIC METHODS                                          ** */
    /* ************************************************************** */
    public async getWebinar(webinarId: string): Promise<string> {
        this.loggerService.info(`[WebinarTelemetryService] getWebinar ({$webinarId})`);
        return await this.webinarTelemetryDatabase.getJSONWebinar(webinarId);
    }

    public async parseLogEntry(logEntry: LogEntry, dataChannel: DataChannel): Promise<WebinarLogEntryDB | undefined> {

        if (logEntry.method.startsWith('getWebinar')) return;
        if (logEntry.method.startsWith('getServerWebinars')) return;
        if (logEntry.method.startsWith('getActiveAndFutureWebinarsRooms')) return;
        if (logEntry.method.startsWith('attachAllListeners')) return;

        // Get the webinarId
        const webinarId = logEntry.params['web'];
        if (!webinarId) return;
        this.loggerService.info(`[WebinarTelemetryService] parseLogEntry (${logEntry.method}, ${logEntry.level})`);

        // Get the webinarDB object
        const webinarDB = await this.getWebinarDB(webinarId);

        if (!this.handlers[logEntry.method]) return;
        const webinarLogEntry = await this.handlers[logEntry.method](webinarDB, logEntry);
        if (webinarId === this.surveyedWebinarId) {
            const notification = this.jsonRPCService.createNotification("webinar.addLogEntry", { webinarId, webinarLogEntry });
            dataChannel.sendMessage(notification.json());
        }
        return webinarLogEntry;
    }

    private populateHandlers() {
        this.handlers["joinWebinar"] = this.joinWebinarHandler;
        this.handlers["startConference"] = this.startConferenceHandler;
        this.handlers["joinConference"] = this.joinConferenceHandler;
        this.handlers["sessionInitiate"] = this.sessionInitiateHandler;
        this.handlers["sessionContentAdd"] = this.sessionContentAddHandler;
        this.handlers["sessionContentRemove"] = this.sessionContentRemoveHandler;
        this.handlers["terminateSession"] = this.terminateSessionHandler;
        this.handlers["leaveWebinar"] = this.leaveWebinarHandler;
        this.handlers["muteParticipant"] = this.muteParticipantHandler;
        this.handlers["putOnStage"] = this.putOnStageHandler;
        this.handlers["shareAudioConfig"] = this.shareAudioConfigHandler;
        //this.handlers["updateStage"] = this.updateStageHandler;
        this.handlers["subscribeToPublisher"] = this.subscribeToPublisherHandler;
        this.handlers["reconnect"] = this.reconnectHandler;
        this.handlers["addSharingMedia"] = this.addSharingMediaHandler;
        this.handlers["removeSharingMedia"] = this.removeSharingMediaHandler;
        this.handlers["askForStaging"] = this.askForStagingHandler;
        this.handlers["declineStagingInvitation"] = this.declineStagingInvitationHandler;
        this.handlers["declineUnmuteInvitation"] = this.declineUnmuteInvitationHandler;
        this.handlers["startWebinar"] = this.startWebinarHandler;
        this.handlers["stopWebinar"] = this.stopWebinarHandler;
        this.handlers["raiseHand"] = this.raiseHandHandler;
        this.handlers["lowerHand"] = this.lowerHandHandler;
        this.handlers["allowAttendeeToTalk"] = this.allowAttendeeToTalkHandler;
        this.handlers["disableAttendeeToTalk"] = this.disableAttendeeToTalkHandler;
        this.handlers["AudioMixer.addBackstageAudio"] = this.audioMixerAddBackstageAudioHandler;
        this.handlers["AudioMixer.setStageAudioLevel"] = this.audioMixerSetStageAudioLevelHandler;
        this.handlers["AudioMixer.addAudioStream"] = this.audioMixerAddAudioStreamHandler;

        this.handlers["onConferenceAdd"] = this.onConferenceAddHandler;
        this.handlers["onAddParticipant"] = this.onNewParticipantHandler;
        this.handlers["onUpdateParticipant"] = this.onUpdateParticipantHandler;
        this.handlers["onNewParticipant"] = this.onNewParticipantHandler;
        this.handlers["onRemovedParticipant"] = this.onRemoveParticipantHandler;
        this.handlers["onAddPublishers"] = this.onAddPublishersHandler;
        this.handlers["onRemovePublishers"] = this.onRemovePublishersHandler;

        this.handlers["onSessionStateChange"] = this.onSessionStateChangeHandler;
        this.handlers["onSessionCreateOffer"] = this.onSessionCreateOfferHandler;
        this.handlers["onSessionSetLocalDescription"] = this.onSessionSetLocalDescriptionHandler;
        this.handlers["onSessionSetRemoteDescription"] = this.onSessionSetRemoteDescriptionHandler;
        this.handlers["onSessionConnected"] = this.onSessionConnectedHandler;
        this.handlers["onSessionTerminated"] = this.onSessionTerminatedHandler;
        this.handlers["onICECandidateError"] = this.onICECandidateErrorHandler;
        this.handlers["onSessionMessage"] = this.onSessionMessageHandler;
        this.handlers["onWebrtcStatsForSession"] = this.onWebrtcStatsForSessionHandler;

        this.handlers["onConferenceRemove"] = this.onConferenceRemoveHandler;
        this.handlers["onStageUpdate"] = this.onStageUpdateHandler;
        this.handlers["onXmppConnectionChangeEvent"] = this.onXmppConnectionChangeHandler;
        this.handlers["onAskStagingMessage"] = this.onAskStagingMessageHandler;
        this.handlers["onMute"] = this.onMuteHandler;
        this.handlers["onUnmute"] = this.onUnmuteHandler;
        this.handlers["onWebinarStart"] = this.onWebinarStartHandler;
        this.handlers["onAllowTalk"] = this.onAllowTalkHandler;
        this.handlers["onDisableTalk"] = this.onDisableTalkHandler;

    }

    private joinWebinarHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        try {
            // Ignore info joinWebinar log entry
            if (logEntry.details.includes("no conference info")) return;

            if (logEntry.level !== LogEntryLevel.ERROR) {

                // Create a new WebinarSession instance
                const sessionDB = this.currentSession = WebinarSessionDB.create(webinarDB);
                this.loggerService.info(`[WebinarTelemetryService] joinWebinarHandler ${JSON.stringify(sessionDB)}`);

                sessionDB.joinDate = logEntry.date.getTime();
                this.loggerService.info(`[WebinarTelemetryService] joinWebinarHandler ${JSON.stringify(sessionDB)} avant`);

                await this.webinarTelemetryDatabase.addSessionToStore(sessionDB);
                this.loggerService.info(`[WebinarTelemetryService] joinWebinarHandler ${JSON.stringify(sessionDB)} apres`);

                const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry);
                const webinarLogEntry = result.webinarLogEntry;

                webinarLogEntry.analysis = "Join webinar";
                if (logEntry.details.includes("start a new conference")) webinarLogEntry.info = "There is no conference for this webinar";
                else webinarLogEntry.info = "A conference already exists for this webinar (created by FROM). ";

                await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
                return webinarLogEntry;
            }
            return undefined;
        }
        catch (error) {
            this.loggerService.error(`[WebinarTelemetryService] joinWebinarHandler (web: ${webinarDB.id}) -- ${stringifyError(error)}`);
        }
    };

    private startConferenceHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Start the conference`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);

        if (!logEntry.details.includes("success") && !logEntry.details.includes("timeout") && logEntry.level !== "ERROR") {
            // Create an "onConferenceAdd" expect event
            //const expectedEvent = WebinarExpectedEvent.create("onConferenceAdd", undefined, "An event 'onConferenceAdd' is expected to confirm the conference creation");
            //this.addExpectedEvent(entry, expectedEvent);
        }
        return webinarLogEntry;
    };

    private joinConferenceHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Join the conference`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);

        if (!logEntry.details.includes("success")) {
            /*
            const expectedEvent = WebinarExpectedEvent.create("onNewParticipant", { part }, `An event 'onNewParticipant' for participant "${this.webinar.localUserName}" is expected`);
            this.addExpectedEvent(webinarLogEntry, expectedEvent);
            const expectedStageEvent = WebinarExpectedEvent.create("onStageUpdate", undefined, "A stage update event is expected");
            this.addExpectedEvent(webinarLogEntry, expectedStageEvent);
            */
        }
        return webinarLogEntry;
    };

    private sessionInitiateHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const media = logEntry.params["media"];
        const type = logEntry.params["type"];
        webinarLogEntry.analysis = `Initiate ${type} session with media "${media}"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        /*const expectedEvent = WebinarExpectedEvent.create("onSessionConnected", { sid }, `An event 'onSessionConnected' with sid: ${sid} is expected to confirm the session connection`);
        this.addExpectedEvent(entry, expectedEvent);
        if (type === "MCU") {
            const expectedAudioEvent = WebinarExpectedEvent.create("AudioMixer.addBackstageAudio", undefined, "An add MCU backstage audio call is expected");
            this.addExpectedEvent(entry, expectedAudioEvent);
        }*/
        return webinarLogEntry;
    };

    private sessionContentAddHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const type = logEntry.params["type"];
        const media = logEntry.params["media"];
        webinarLogEntry.analysis = `Session ContentAdd (${type}) with media "${media}"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);

        return webinarLogEntry;
    };

    private sessionContentRemoveHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const type = logEntry.params["type"];
        const media = logEntry.params["media"];
        webinarLogEntry.analysis = `Session ContentRemove (${type}) with media "${media}"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private terminateSessionHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const reason = logEntry.params["reason"];
        webinarLogEntry.analysis = `Session terminate for "--Participant--" (reason: ${reason})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private leaveWebinarHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        // Only handle success
        if (!logEntry.details.includes("success")) return;

        // Update session end date
        this.currentSession.leaveDate = logEntry.date.getTime();
        const reason = logEntry.params["reason"];
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Leave the conference (${reason})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        await this.webinarTelemetryDatabase.updateStoreSession(this.currentSession);
        return webinarLogEntry;
    };

    private muteParticipantHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const mutedParticipantId = logEntry.params["part"];
        const muteSFU = logEntry.params["muteSFU"] === "true";
        const muteMCU = logEntry.params["muteMCU"] === "true";
        const requestId = logEntry.params["sfuRequestId"] || logEntry.params["mcuRequestId"];
        webinarLogEntry.userState.mutedSFU = muteSFU;
        webinarLogEntry.userState.mutedMCU = muteMCU;
        webinarLogEntry.requestId = requestId;
        const mute = webinarLogEntry.userState.mutedSFU && webinarLogEntry.userState.mutedMCU;
        if (mutedParticipantId === webinarDB?.webinarRB?.session?.localParticipant?.id)
            webinarLogEntry.analysis = `${mute ? "Mute" : "Unmute"} participant "--Participant--" (muteSFU: ${muteSFU}, muteMCU: ${muteMCU})`;
        else webinarLogEntry.analysis = `Invite "--Participant--" to ${mute ? "mute" : "unmute"}`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private putOnStageHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const media = logEntry.params["type"];
        const staged = logEntry.params["stage"];
        webinarLogEntry.analysis = `Put "--Participant--" ${media === "participant" ? "audioVideo" : "sharing"} ${staged === "true" ? "on stage" : "backstage"}`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private shareAudioConfigHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const audioConfig = logEntry.params["audioConfig"];
        webinarLogEntry.userState.audioConfig = audioConfig;
        webinarLogEntry.analysis = `"--Participant--" audible from the ${audioConfig}`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private updateStageHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const media = logEntry.params["media"];
        const state = logEntry.params["state"];
        webinarLogEntry.analysis = `updateStage "--Participant--" (media: ${media}, state: ${state})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private subscribeToPublisherHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const type = logEntry.params["type"] === "sharing" ? "sharing" : "audioVideo";
        webinarLogEntry.analysis = `SubscribeToPublisher "--Participant--" (type: ${type})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private reconnectHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        if (logEntry.details.includes("active webinar")) {
            const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
            const webinarLogEntry = result.webinarLogEntry;
            webinarLogEntry.analysis = "Webinar reconnected ";
            await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
            return webinarLogEntry;
        }
        return undefined;
    };

    private addSharingMediaHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Add sharing media`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private removeSharingMediaHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Remove sharing media`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private askForStagingHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Invite "--Participant--" to go on stage`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private declineUnmuteInvitationHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Decline unmute invitation received from "--Participant--"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private startWebinarHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.setMajor();
        webinarLogEntry.analysis = `"--Participant--" start the webinar `;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private stopWebinarHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.setMajor();
        webinarLogEntry.analysis = `"--Participant--" stop the webinar `;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private raiseHandHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `"--Participant--" raise hand `;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private lowerHandHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `"--Participant--" raise hand `;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private allowAttendeeToTalkHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Allow "--Participant--" to talk `;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private disableAttendeeToTalkHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Forbid "--Participant--" to talk `;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private audioMixerAddBackstageAudioHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const device = logEntry.params["device"];
        webinarLogEntry.analysis = `Add backstage audioPlayer (device: ${device})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private declineStagingInvitationHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Decline staging information received from "--Participant--"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private audioMixerSetStageAudioLevelHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const audioLevel = logEntry.params["audioLevel"];
        webinarLogEntry.userState.stageAudioLevel = Number(parseFloat(audioLevel).toFixed(2));
        webinarLogEntry.analysis = `Set stage audioLevel (audioLevel: ${webinarLogEntry.userState.stageAudioLevel})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private audioMixerAddAudioStreamHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `Add stage audioPlayer "--Participant--"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onConferenceAddHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = "⤷ ConferenceAdd";
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onNewParticipantHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const newParticipantId = logEntry.params["part"];
        if (newParticipantId === webinarDB.userId) {
            if (logEntry.params["mute"] === "true") webinarLogEntry.userState.mutedSFU = webinarLogEntry.userState.mutedMCU = true;
        }
        webinarLogEntry.analysis = `⤷ NewParticipant : "--Participant--" (mute: ${logEntry.params["mute"]})`;
        /*const expectedStageEvent = WebinarExpectedEvent.create("onStageUpdate", undefined, "A stage update event is expected");
        this.addExpectedEvent(webinarLogEntry, expectedStageEvent);*/
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onRemoveParticipantHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ RemoveParticipant : "--Participant--"`;
        /*const expectedStageEvent = WebinarExpectedEvent.create("onStageUpdate", undefined, "A stage update event is expected");
        this.addExpectedEvent(webinarLogEntry, expectedStageEvent);*/
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onUpdateParticipantHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const newParticipantId = logEntry.params["part"];
        if (newParticipantId === webinarDB.userId) {
            if (logEntry.params["mute"] === "true") webinarLogEntry.userState.mutedSFU = webinarLogEntry.userState.mutedMCU = true;
        }
        webinarLogEntry.analysis = `⤷ UpdateParticipant : "--Participant--" (mute: ${logEntry.params["mute"]}, role: ${logEntry.params["role"]})`;
        /*const expectedStageEvent = WebinarExpectedEvent.create("onStageUpdate", undefined, "A stage update event is expected");
        this.addExpectedEvent(webinarLogEntry, expectedStageEvent);*/
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onAddPublishersHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ AddPublisher : "--Participant--"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onRemovePublishersHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const media = logEntry.params["media"];
        webinarLogEntry.analysis = `⤷ RemovePublisher : "--Participant--" (media: ${media})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    /* ************************************************** */
    /* ** JINGLE STUFF                                 ** */
    /* ************************************************** */

    private onSessionStateChangeHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const signalingState = logEntry.params['signalingState'];
        const connectionState = logEntry.params['connectionState'];
        const iceConnectionState = logEntry.params['iceConnectionState'];
        webinarLogEntry.analysis = `⤷ SessionStateChange (conn: "${connectionState}", ice: "${iceConnectionState}", signaling: "${signalingState}")`;
        webinarLogEntry.filter = 'jingle';
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onSessionCreateOfferHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ CreateOffer`;
        webinarLogEntry.filter = 'jingle';
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onSessionSetLocalDescriptionHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ SetLocalDescription`;
        webinarLogEntry.filter = 'jingle';
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onSessionSetRemoteDescriptionHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ SetRemoteDescription`;
        webinarLogEntry.filter = 'jingle';
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onSessionConnectedHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ SessionConnected : "--Participant--"`;
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onSessionMessageHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const event = logEntry.params["evt"];
        switch (event) {
            case 'jingleSessionInfoRinging': webinarLogEntry.analysis = `⤷ SessionInfoRinging`; break;
            case 'jingleSessionAccept': webinarLogEntry.analysis = `⤷ SessionAccept`; break;
            default: webinarLogEntry.analysis = `⤷ ${event}`; break;
        }
        webinarLogEntry.filter = 'jingle';
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onWebrtcStatsForSessionHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ onWebrtcStats`;
        webinarLogEntry.rawDataType = "json";
        webinarLogEntry.rawData = JSON.stringify(logEntry.params['data'], null, 2);
        webinarLogEntry.filter = 'stats';
        delete logEntry.params['data'];
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onICECandidateErrorHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, false);
        const webinarLogEntry = result.webinarLogEntry;
        const info = logEntry.params["info"];
        const type = logEntry.params["type"];
        if (type === 'TURN/STUN') webinarLogEntry.analysis = `ICECandidateError TURN/STUN ERROR ${info})`;
        else webinarLogEntry.analysis = `⤷ ICECandidateError SERVER NOT REACHABLE ${info}`;
        webinarLogEntry.filter = 'jingle';
        webinarLogEntry.level = 'error';
        await this.webinarTelemetryDatabase.addLogEntryToStore(webinarLogEntry);
        return webinarLogEntry;
    };

    private onSessionTerminatedHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const media = logEntry.params["media"];
        const reason = logEntry.params["reason"];
        webinarLogEntry.analysis = `⤷ SessionTerminated : "--Participant--" (media: ${media}, reason: ${reason})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onConferenceRemoveHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.setMajor();
        webinarLogEntry.analysis = "⤷ Webinar broadcast stop (ConferenceRemove)";
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onStageUpdateHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const simulated = logEntry.params["simulated"] === "true" ? " (simulated)" : "";
        webinarLogEntry.analysis = `⤷ StageUpdate ${simulated}`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onXmppConnectionChangeHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const connectionStates: any = { ON_XMPP_DISCONNECTED: "disconnected", ON_XMPP_CONNECTING: "connection in progress", ON_XMPP_CONNECTED: "reconnected" };
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const event = connectionStates[logEntry.params["event"]];
        let reason = logEntry.params["reason"].toLowerCase();
        reason = (reason === "unknown") ? "" : `(${reason})`;
        webinarLogEntry.setMajor();
        webinarLogEntry.analysis = `∅ Network "${event}" ${reason}`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onAskStagingMessageHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const participantId = logEntry.params["part"];
        const command = logEntry.params["command"];
        if (command === "refuse-staging") webinarLogEntry.analysis = `⤷ "--Participant--" refuses the invitation to go on stage (part: ${participantId})`;
        else webinarLogEntry.analysis = `⤷ Receive invitation to go on stage from "--Participant--" (part: ${participantId})`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onMuteHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ "--Participant--" is muted`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onUnmuteHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        const askById = logEntry.params["askBy"];
        if (!askById) webinarLogEntry.analysis = `⤷ "--Participant--" is unmuted`;
        else webinarLogEntry.analysis = `⤷ Receive invitation to unmute from "--AskBy--"`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onWebinarStartHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.setMajor();
        webinarLogEntry.analysis = "⤷ Webinar broadcast start";
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onAllowTalkHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ "--Participant--" is allowed to talk`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private onDisableTalkHandler = async (webinarDB: WebinarDB, logEntry: LogEntry): Promise<WebinarLogEntryDB | undefined> => {
        const result = await this.getOrCreateWebinarLogEntryDB(webinarDB, logEntry, true);
        const webinarLogEntry = result.webinarLogEntry;
        webinarLogEntry.analysis = `⤷ "--Participant--" is forbidden to talk`;
        await this.webinarTelemetryDatabase[result.create ? "addLogEntryToStore" : "updateStoreLogEntry"](webinarLogEntry);
        return webinarLogEntry;
    };

    private async getWebinarDB(webinarId: string): Promise<WebinarDB> {
        if (this.ongoingWebinarPromises[webinarId]) return this.ongoingWebinarPromises[webinarId];
        this.ongoingWebinarPromises[webinarId] = this.getWebinarDBInternal(webinarId).finally(() => { delete this.ongoingWebinarPromises[webinarId]; }); ;
        return this.ongoingWebinarPromises[webinarId];
    }

    private async getWebinarDBInternal(webinarId: string): Promise<WebinarDB> {
        try {
            // Current webinar is the expected webinar : use it
            if (this.currentWebinar?.id === webinarId) return this.currentWebinar;

            // Use already know webinarArray
            if (this.webinarIds[webinarId]) {
                this.currentWebinar = this.webinarIds[webinarId];
                return this.currentWebinar;
            }

            const webinarRB = await this.webinarServiceRB.getWebinar(webinarId);

            // Check if webinar already in DB;
            let webinarDB = await this.webinarTelemetryDatabase.getWebinarFromStore(webinarId);
            if (webinarDB) {
                webinarDB.webinarRB = webinarRB;
                webinarDB.userId = webinarRB.session?.localParticipant?.id;
                this.webinarIds[webinarId] = webinarDB;
                this.currentWebinar = this.webinarIds[webinarId];
                return webinarDB;
            }

            // If not create and store webinarDB object
            webinarDB = WebinarDB.createFromWebinarRB(webinarRB);
            await this.webinarTelemetryDatabase.addWebinarToStore(webinarDB);
            this.webinarIds[webinarId] = webinarDB;
            this.currentWebinar = this.webinarIds[webinarId];
            return webinarDB;
        }
        catch (error) {
            this.loggerService.error(`[WebinarTelemetryService] getWebinarDB (web: ${webinarId}) -- ${JSON.stringify(error)}`);
            throw error;
        }
    }

    private async getOrCreateWebinarLogEntryDB(webinarDB: WebinarDB, logEntry: LogEntry, isEvent: boolean = false): Promise<{ webinarLogEntry: WebinarLogEntryDB; create: boolean; }> {
        const correlatorId = logEntry.params["correlatorId"];
        if (correlatorId) {
            const webinarLogEntry = await this.webinarTelemetryDatabase.getLogByCorrelatorId(correlatorId);
            if (webinarLogEntry) {
                webinarLogEntry.requestId = logEntry.params["requestId"];
                webinarLogEntry.updateUserState(webinarDB);
                webinarLogEntry.sid = logEntry.params["sid"];
                webinarLogEntry.level = logEntry.level;
                return { webinarLogEntry, create: false };
            }
        }

        // Create a new webinarLogEntry
        const webinarLogEntry = WebinarLogEntryDB.createFromLogEntry(webinarDB, this.currentSession, logEntry);
        webinarLogEntry.type = isEvent ? 'event' : 'action';
        webinarLogEntry.updateUserState(webinarDB);
        webinarLogEntry.sid = logEntry.params["sid"];
        return { webinarLogEntry, create: true };
    }

}
